import React from "react";
import {Modal, ProgressBar, Spinner, Table, Alert} from "react-bootstrap";
import {formatDateTime} from "../utils/Utils";

/**
 * SyncProgressDialog - Shows sync operation progress and results
 *
 * Props:
 * - show: boolean - whether to show the dialog
 * - onClose: function - callback when dialog is closed
 * - syncType: string - 'push' or 'pull'
 * - isInProgress: boolean - whether sync is in progress
 * - syncResult: object - result of sync operation
 */
export function SyncProgressDialog({show, onClose, syncType, isInProgress, syncResult}) {
    const getTitle = () => {
        if (syncType === 'push') {
            return isInProgress ? 'Pushing to Server...' : 'Push Complete';
        } else {
            return isInProgress ? 'Pulling from Server...' : 'Pull Complete';
        }
    };

    const getIcon = () => {
        if (syncType === 'push') {
            return 'fa-cloud-upload-alt';
        } else {
            return 'fa-cloud-download-alt';
        }
    };

    const renderProgress = () => (
        <div className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" style={{width: '3rem', height: '3rem'}} />
            <h5 className="mb-3">
                <i className={`fas ${getIcon()} me-2`}></i>
                {syncType === 'push' ? 'Pushing data to server...' : 'Pulling data from server...'}
            </h5>
            <ProgressBar animated now={100} variant="primary" className="mt-3" />
            <p className="text-muted mt-3">Please wait, this may take a moment.</p>
        </div>
    );

    const renderResult = () => {
        if (!syncResult) return null;

        const isSuccess = syncResult.success;
        const alertVariant = isSuccess ? 'success' : 'danger';
        const alertIcon = isSuccess ? 'fa-check-circle' : 'fa-exclamation-circle';

        return (
            <div className="py-3">
                <Alert variant={alertVariant} className="d-flex align-items-center">
                    <i className={`fas ${alertIcon} me-2 fs-3`}></i>
                    <div>
                        <strong>{isSuccess ? 'Sync Successful!' : 'Sync Failed'}</strong>
                        <p className="mb-0 small">{syncResult.message}</p>
                    </div>
                </Alert>

                {syncType === 'push' && syncResult.totalProcessed !== undefined && (
                    <Table bordered hover className="mt-3 mb-0">
                        <tbody>
                            <tr>
                                <td><i className="fas fa-list-ol me-2 text-primary"></i>Total Processed</td>
                                <td className="text-end fw-bold">{syncResult.totalProcessed}</td>
                            </tr>
                            <tr>
                                <td><i className="fas fa-check me-2 text-success"></i>Successful</td>
                                <td className="text-end fw-bold text-success">{syncResult.successCount || 0}</td>
                            </tr>
                            <tr>
                                <td><i className="fas fa-times me-2 text-danger"></i>Failed</td>
                                <td className="text-end fw-bold text-danger">{syncResult.failedCount || 0}</td>
                            </tr>
                        </tbody>
                    </Table>
                )}

                {syncType === 'pull' && (
                    <Table bordered hover className="mt-3 mb-0">
                        <tbody>
                            <tr>
                                <td><i className="fas fa-download me-2 text-primary"></i>Total Received</td>
                                <td className="text-end fw-bold">{syncResult.totalCount || 0}</td>
                            </tr>
                            <tr>
                                <td><i className="fas fa-plus me-2 text-info"></i>Inserted</td>
                                <td className="text-end fw-bold text-info">{syncResult.insertedCount || 0}</td>
                            </tr>
                            <tr>
                                <td><i className="fas fa-sync me-2 text-warning"></i>Updated</td>
                                <td className="text-end fw-bold text-warning">{syncResult.updatedCount || 0}</td>
                            </tr>
                            <tr>
                                <td><i className="fas fa-trash me-2 text-danger"></i>Deleted</td>
                                <td className="text-end fw-bold text-danger">{syncResult.deletedCount || 0}</td>
                            </tr>
                        </tbody>
                    </Table>
                )}

                {syncResult.syncTime && (
                    <p className="text-muted small mt-3 mb-0 text-center">
                        <i className="fas fa-clock me-1"></i>
                        Completed at: {formatDateTime(syncResult.syncTime)}
                    </p>
                )}
            </div>
        );
    };

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            backdrop="static"
            keyboard={!isInProgress}
        >
            <Modal.Header closeButton={!isInProgress}>
                <Modal.Title>
                    <i className={`fas ${getIcon()} me-2`}></i>
                    {getTitle()}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isInProgress ? renderProgress() : renderResult()}
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                    disabled={isInProgress}
                >
                    {isInProgress ? 'Please wait...' : 'Close'}
                </button>
            </Modal.Footer>
        </Modal>
    );
}
